/**
 * Drop outcomes the user cannot actually bet at.
 *
 * The odds feed can list an outcome without pricing it: `apiFootballOddsMapping` persists
 * `odd: Number(v.odd) || 0` and only discards a market whose `values` array is empty, never one
 * whose values are all zero. Untouched, such an outcome renders as a tappable tile reading `0.00`,
 * which reads as a real price and is not one.
 *
 * The server already treats these as unpriced everywhere it matters — `getMarketValuesFromOdds`
 * keeps only `odd > 0`, and both create and submit-picks require `clientOdd > 0` before trusting a
 * client-sent price — so this is about not offering the bet, not about correctness of settlement.
 *
 * Measured on staging (2026-08-06): 0 of 3,905 markets across 143 upcoming matches were affected.
 * This is a guard against a shape the feed permits, not a fix for something currently happening.
 */

import type { MarketOdds } from '../types/create.types'

/** A price a user can bet at. `0` / `null` / `NaN` mean the outcome was listed but never priced. */
export function isPriceableOdd(odd: unknown): boolean {
  return typeof odd === 'number' && Number.isFinite(odd) && odd > 0
}

/**
 * Keep only priceable outcomes, and drop any market left with nothing to pick.
 *
 * Dropping the whole market matters as much as dropping the tile: an empty market still renders a
 * chip in the strip (`InlineMarketPicker`), so the user gets a tab that opens onto nothing.
 */
export function dropUnpriceableMarkets(markets: MarketOdds[]): MarketOdds[] {
  const result: MarketOdds[] = []
  for (const market of markets) {
    const values = (market?.values ?? []).filter((v) => isPriceableOdd(v?.odd))
    if (values.length === 0) continue
    // Preserve identity when nothing was removed, so memoised consumers don't see a new array.
    result.push(values.length === market.values.length ? market : { ...market, values })
  }
  return result
}
